import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle2, XCircle, Clock, MapPin, Image, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function Approvals() {
  const { api, currentUser } = useApp();
  const [approvals, setApprovals] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getApprovals({ role: currentUser?.role });
      setApprovals(res.data);
    } catch (e) { console.error(e); }
  }, [api, currentUser]);

  useEffect(() => { load(); }, [load]);

  const filtered = approvals.filter(a => {
    if (activeTab === 'pending') return a.overall_status === 'pending';
    if (activeTab === 'approved') return a.overall_status === 'approved';
    if (activeTab === 'rejected') return a.overall_status === 'rejected';
    return true;
  });

  const handleApprove = async (approvalId) => {
    try {
      await api.approveRequest(approvalId, { action: 'approve', user_id: currentUser.id });
      toast.success('Approved successfully');
      load();
    } catch (e) { toast.error('Failed to approve'); }
  };

  const openReject = (id) => { setRejectingId(id); setRejectReason(''); setShowRejectDialog(true); };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a reason for rejection'); return; }
    try {
      await api.rejectRequest(rejectingId, { action: 'reject', user_id: currentUser.id, reason: rejectReason });
      toast.success('Rejected with reason');
      setShowRejectDialog(false);
      load();
    } catch (e) { toast.error('Failed to reject'); }
  };

  const statusIcon = (s) => {
    if (s === 'approved') return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (s === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-600" />;
  };

  const statusBadge = (s) => {
    if (s === 'approved') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s === 'rejected') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  const pendingCount = approvals.filter(a => a.overall_status === 'pending').length;

  return (
    <div data-testid="approvals-page" className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Approval Requests</h1>
        <p className="text-sm text-slate-500 mt-1">{pendingCount} pending approvals</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            Pending ({approvals.filter(a => a.overall_status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            Approved ({approvals.filter(a => a.overall_status === 'approved').length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            Rejected ({approvals.filter(a => a.overall_status === 'rejected').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <div className="space-y-4">
            {filtered.map(a => (
              <Card key={a.id} data-testid={`approval-card-${a.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Photo preview */}
                      <div className="w-16 h-16 rounded-lg border border-border/40 overflow-hidden bg-slate-100 flex-shrink-0">
                        {a.place_data?.selfie_photo ? (
                          <img src={a.place_data.selfie_photo} alt="Selfie" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-slate-300" /></div>
                        )}
                      </div>

                      <div>
                        <h3 className="text-base font-bold text-slate-900">{a.place_data?.name || 'Unnamed'}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-slate-500">by {a.requester_name}</span>
                          <Badge variant="secondary" className="text-[10px]">{ROLE_LABELS[a.requester_role] || a.requester_role}</Badge>
                        </div>
                        {a.place_data?.lat && a.place_data?.lng && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                            <MapPin className="w-3 h-3" />
                            {a.place_data.lat.toFixed(4)}, {a.place_data.lng.toFixed(4)}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{a.place_data?.city} {a.place_data?.zone && `- ${a.place_data.zone}`}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${statusBadge(a.overall_status)}`}>{a.overall_status}</Badge>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedApproval(a)}>
                        View <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>

                  {/* Approval Chain */}
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {a.approval_chain?.map((step, i) => (
                      <React.Fragment key={i}>
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border
                          ${step.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            step.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                            i === a.current_step ? 'bg-amber-50 text-amber-700 border-amber-300 ring-1 ring-amber-300' :
                            'bg-slate-50 text-slate-500 border-slate-200'}`}
                        >
                          {statusIcon(step.status)}
                          {ROLE_LABELS[step.role] || step.role}
                        </div>
                        {i < a.approval_chain.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Actions for pending */}
                  {a.overall_status === 'pending' && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border/30">
                      <Button data-testid={`approve-btn-${a.id}`} size="sm" onClick={() => handleApprove(a.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button data-testid={`reject-btn-${a.id}`} size="sm" variant="outline" onClick={() => openReject(a.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50">
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && (
              <Card className="border border-border/60"><CardContent className="p-8 text-center text-slate-400">No {activeTab} approvals</CardContent></Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent data-testid="reject-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Reject Location
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-semibold text-slate-700 block mb-2">Reason for Rejection</label>
            <Textarea
              data-testid="reject-reason-input"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this business location..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-reject-btn" onClick={handleReject} className="bg-red-600 hover:bg-red-700 text-white">
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedApproval} onOpenChange={() => setSelectedApproval(null)}>
        <DialogContent data-testid="approval-detail-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Location Details</DialogTitle>
          </DialogHeader>
          {selectedApproval && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-400">Name</p><p className="text-sm font-semibold">{selectedApproval.place_data?.name}</p></div>
                <div><p className="text-xs text-slate-400">City</p><p className="text-sm font-semibold">{selectedApproval.place_data?.city || '-'}</p></div>
                <div><p className="text-xs text-slate-400">Requested By</p><p className="text-sm font-semibold">{selectedApproval.requester_name}</p></div>
                <div><p className="text-xs text-slate-400">Status</p><Badge className={`text-xs ${statusBadge(selectedApproval.overall_status)}`}>{selectedApproval.overall_status}</Badge></div>
              </div>
              {/* Photos */}
              <div className="grid grid-cols-2 gap-3">
                {selectedApproval.place_data?.selfie_photo && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Selfie</p>
                    <img src={selectedApproval.place_data.selfie_photo} alt="Selfie" className="w-full rounded-lg border" />
                  </div>
                )}
                {selectedApproval.place_data?.place_photo && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Place Photo</p>
                    <img src={selectedApproval.place_data.place_photo} alt="Place" className="w-full rounded-lg border" />
                  </div>
                )}
              </div>
              {selectedApproval.place_data?.lat && (
                <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                  Coordinates: {selectedApproval.place_data.lat.toFixed(6)}, {selectedApproval.place_data.lng.toFixed(6)}
                </div>
              )}
              {/* Chain detail */}
              <div>
                <p className="text-xs text-slate-400 mb-2">Approval Chain</p>
                <div className="space-y-2">
                  {selectedApproval.approval_chain?.map((step, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded-lg border ${step.status === 'approved' ? 'bg-emerald-50 border-emerald-200' : step.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-center gap-2">
                        {statusIcon(step.status)}
                        <span className="text-sm font-medium">{ROLE_LABELS[step.role] || step.role}</span>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-[10px]">{step.status}</Badge>
                        {step.reason && <p className="text-[10px] text-red-500 mt-0.5">{step.reason}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
